import { useState, useRef, useEffect, useContext } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css"; // Import KaTeX CSS for styling
import { useDispatch, useSelector } from "react-redux";
import axios from "./../../../utils/axiosInstance";
import {
  Send,
  PanelLeftClose,
  PanelLeft,
  PlusCircle,
  FullscreenIcon,
} from "lucide-react";
import { FaRegUser } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FaRegStopCircle, FaMicrophone } from "react-icons/fa";
import "./Chat.css";
import { updateNode } from "../../../utils/flowSlice";

import { BiLoader } from "react-icons/bi";
import { DarkModeContext } from "../../../context/DarkModeContext";

import { IoMdArrowDropdown } from "react-icons/io";
import { SiClaude, SiOpenai } from "react-icons/si";
import DeepseekIcon from "../../deepseek-stroke-rounded";
import toast from "react-hot-toast";

const ChatInterface = ({
  data,
  data_messages,
  data_chatHistory,
  chatNodeId,
}) => {
  // const { setCreditsUsed } = data;
  const { darkMode } = useContext(DarkModeContext);
  const [selectedModel, setSelectedModel] = useState("Claude 3.7");
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);
  const audioContext = useRef(null);
  const analyser = useRef(null);
  const dataArray = useRef(null);
  const animationId = useRef(null);
  const [messages, setMessages] = useState(data_messages || []);
  const [chatHistory, setChatHistory] = useState(data_chatHistory || []);
  const [input, setInput] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false); // Track fullscreen state
  const chatContainerRef = useRef(null); // Reference to the main chat container
  const textAreaRef = useRef(null); // Create a ref for the textarea
  const [scriptArray, setScriptArray] = useState([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const edges = useSelector((store) => store.flow.edges);
  const nodes = useSelector((store) => store.flow.nodes);

  // function getting script from linked nodes
  const getScript = () => {
    const currentEdges = edges.filter((edge) => edge.target === chatNodeId);
    const sourceNodeIds = currentEdges.map((edge) => {
      return edge.source;
    });
    const sourceNodes = nodes.filter((node) => sourceNodeIds.includes(node.id));
    const targetNode = nodes.find((node) => node.id === chatNodeId);
    const newScriptArray = sourceNodes.map((node) =>
      node.type === "groupNode"
        ? { id: node.id, type: node.type, scriptArray: node.data.scriptArray }
        : {
            id: node.id,
            type: node.type,
            script: node.data.script,
            note: node.data.note,
          }
    );
    setScriptArray(newScriptArray);
    dispatch(
      updateNode({
        id: chatNodeId,
        data: {
          ...targetNode.data,
          scriptArray: newScriptArray,
        },
      })
    );
  };

  const handleSaveChat = () => {
    if (messages.length > 0) {
      // Save the current conversation to the history with the current time.
      const newChatHistory = {
        model: selectedModel,
        color:
          selectedModel === "Claude 3.7"
            ? "bg-orange-900/20 text-orange-400"
            : selectedModel === "GPT-4o"
            ? "bg-green-900 text-green-400"
            : "bg-blue-900 text-blue-400", // You can customize based on the model or other criteria
        time: new Date().toLocaleString(),
        content: messages,
        // title: getTitle(),
        title: messages[0]?.text?.slice(0, 10),
      };

      // Update the chat history state
      setChatHistory([...chatHistory, newChatHistory]);
    }
  };

  const handleNewChat = () => {
    handleSaveChat();
    setMessages([]);
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (e.target.scrollHeight > 36) {
      e.target.style.height = `${e.target.scrollHeight}px`;
    }
    if (e.target.value === "") {
      e.target.style.height = `${36}px`;
    }
  };

  const handleEnterPress = async (event) => {
    getScript();
    if (event.key === "Enter") {
      if (event.shiftKey) {
        return;
      } else {
        event.preventDefault();
        sendAndReceiveMsg();
      }
    }
  };

  const sendAndReceiveMsg = async () => {
    setInput("");
    const userMessage = textAreaRef.current.value;
    if (userMessage.trim()) {
      const newMessage = {
        role: "user",
        text: userMessage.trim(),
        time: new Date().toLocaleString(),
      };
      setMessages((prevMessages) => [...prevMessages, newMessage]);
      const content = JSON.stringify({
        messages: [...messages, newMessage],
        linkedNodeInfo: scriptArray,
      });
      textAreaRef.current.style.height = "30px";
      setLoading(true);
      try {
        // Use fetch instead of axios for streaming
        const response = await fetch(
          `${process.env.REACT_APP_BASED_URL}/service/chat`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: localStorage.getItem("token"),
            },
            body: JSON.stringify({
              content: content,
              model: selectedModel,
            }),
          }
        );

        if (!response.body) {
          throw new Error("Stream response body is not available.");
        }
        setLoading(false);
        // Read the stream incrementally
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullMessage = "";
        let last = Date.now();
        // Process each chunk as it arrives
        while (true) {
          // console.log("full: ", fullMessage)
          const { done, value } = await reader.read();
          if (done) break;

          // Decode the chunk and append to fullMessage
          const chunkStr = decoder.decode(value, { stream: true });
          // console.log("chunk: ", chunkStr);
          fullMessage += chunkStr;

          // Update the bot message with the streamed content

          const botMsg = {
            id: last,
            role: "bot",
            text: fullMessage.trim(), // Update with full message as it's being received
            time: new Date().toLocaleString(),
          };
          setMessages((prevMessages) => {
            const filteredMessages = prevMessages.filter(
              (msg) => msg.id !== last
            );
            return [...filteredMessages, botMsg];
          });
        }
        const i_token = Math.ceil(content.length / 3.5);
        const o_token = Math.ceil(fullMessage.length / 3.5);
        console.log(i_token, o_token, process.env.GPT_I);
        const creditsUsed =
          selectedModel === "GPT-4o"
            ? i_token / 1200 + o_token / 300
            : selectedModel === "Claude 3.7"
            ? i_token / 1000 + o_token / 200
            : i_token / 8000 + o_token / 2000;
        const token = localStorage.getItem("token");
        console.log(creditsUsed);
        const response2 = await axios.post(
          `${process.env.REACT_APP_BASED_URL}/payment/updateCredits`,
          { creditsUsed: creditsUsed }
        );
        const creditsUsedTotal = response2.data.creditsUsedTotal;
        localStorage.setItem("creditsUsedTotal", creditsUsedTotal);
        // setCreditsUsed(creditsUsedTotal);
      } catch (error) {
        console.error("Error while fetching the stream:", error);
        if (error.status === 402) {
          toast.error(
            "You have run out of your credit. You need to upgrade your plan"
          );
        }
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    dispatch(
      updateNode({
        id: data.id,
        data: { ...data, messages: messages, chatHistory: chatHistory },
      })
    );
  }, [messages, chatHistory]);

  const getTitle = async () => {
    // const script = JSON.stringify(messages);
    // const response = await axios.post(
    //   `${process.env.REACT_APP_BASED_URL}/service/title`,
    //   {
    //     script,
    //   }
    // );
    // const tempTitle = response.data;
    // return tempTitle;
    return "";
  };

  // Let scrollbar to the latest message
  useEffect(() => {
    let top = 0;
    for (let i = 0; i < messages.length; i++) {
      top +=
        10 *
        document.getElementById(`${chatNodeId}${i}`).getBoundingClientRect()
          .height;
    }
    document.getElementById(`${chatNodeId}chatMessages`).scrollTo({
      top: top,
      behavior: "smooth",
    });
  }, [messages]);

  const fetchScriptFromVoice = async (audioBlob) => {
    try {
      const formData = new FormData();
      formData.append("file", audioBlob, "audio.mp3");
      formData.append("model", "whisper-1");
      const headers = {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
      };
      const openAiResponse = await axios.post(
        "https://api.openai.com/v1/audio/transcriptions",
        formData,
        {
          headers: headers,
        }
      );
      const script = openAiResponse.data.text;
      setInput(script);
      setIsConverting(false);
    } catch (error) {
      console.error("Error fetching transcription:", error);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setIsRecording(true);

      // Initialize MediaRecorder
      mediaRecorder.current = new MediaRecorder(stream);
      audioChunks.current = [];

      mediaRecorder.current.ondataavailable = (event) => {
        audioChunks.current.push(event.data);
      };

      mediaRecorder.current.onstop = async () => {
        const audioBlob = new Blob(audioChunks.current, { type: "audio/webm" });
        setIsConverting(true);
        fetchScriptFromVoice(audioBlob);
      };

      // Initialize Web Audio API
      audioContext.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      const source = audioContext.current.createMediaStreamSource(stream);
      analyser.current = audioContext.current.createAnalyser();

      source.connect(analyser.current);
      analyser.current.fftSize = 256; // Smaller fftSize for better resolution
      dataArray.current = new Uint8Array(analyser.current.frequencyBinCount);

      mediaRecorder.current.start(); // Start recording
    } catch (error) {
      console.error("Error accessing microphone:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current) {
      mediaRecorder.current.stop();
    }
    if (audioContext.current) {
      audioContext.current.close();
    }

    cancelAnimationFrame(animationId.current); // Stop the animation
    setIsRecording(false);

    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      // Enter fullscreen
      if (chatContainerRef.current.requestFullscreen) {
        chatContainerRef.current.requestFullscreen();
      } else if (chatContainerRef.current.mozRequestFullScreen) {
        // Firefox
        chatContainerRef.current.mozRequestFullScreen();
      } else if (chatContainerRef.current.webkitRequestFullscreen) {
        // Chrome, Safari
        chatContainerRef.current.webkitRequestFullscreen();
      } else if (chatContainerRef.current.msRequestFullscreen) {
        // IE/Edge
        chatContainerRef.current.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        // Firefox
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        // Chrome, Safari
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        // IE/Edge
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false); // Exit fullscreen
      } else {
        setIsFullscreen(true); // Enter fullscreen
      }
    };

    // Add event listener for fullscreen change
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange); // For Safari/Chrome
    document.addEventListener("mozfullscreenchange", handleFullscreenChange); // For Firefox
    document.addEventListener("msfullscreenchange", handleFullscreenChange); // For IE/Edge

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  const handleHistoryDelClick = (idx) => {
    const newChatHistory = [...chatHistory]; // Create a copy of the current chatHistory
    newChatHistory.splice(idx, 1); // Remove the item at the given index
    setChatHistory(newChatHistory); // Update state with the new array
  };

  const CustomSelect = ({ options, value, onChange, darkMode }) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleOptionClick = (optionValue) => {
      onChange(optionValue);
      setIsOpen(false);
    };

    return (
      <div
        className={`flex flex-row rounded-3xl relative w-[130px] ${
          darkMode
            ? "bg-transparent text-neutral-950 hover:bg-neutral-200"
            : "bg-transparent text-neutral-300 hover:bg-neutral-500"
        } text-[12px] focus:outline-none focus:ring-0`}
      >
        <button
          className={`flex flex-row gap-1 items-center w-full text-left`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <IoMdArrowDropdown
            className={`size-6 pl-2 items-center justify-center ${
              darkMode
                ? "bg-transparent text-neutral-950"
                : "bg-transparent text-neutral-400"
            }`}
          />
          {value === "Claude 3.7" && (
            <div className="flex w-4 h-4 rounded-full bg-orange-400 items-center justify-center">
              <SiClaude size={"12px"} color="white" />
            </div>
          )}
          {value === "GPT-4o" && (
            <div className="flex w-4 h-4 rounded-full bg-neutral-600 items-center justify-center">
              <SiOpenai size={"12px"} color="white" />
            </div>
          )}
          {value === "DeepSeek" && (
            <div className="flex w-4 h-4 rounded-full bg-slate-300 items-center justify-center border border-blue-600">
              <DeepseekIcon />
            </div>
          )}
          {options.find((option) => option.value === value)?.text ||
            "Select an option"}
        </button>
        {isOpen && (
          <ul
            className={`mt-1 rounded-xl absolute top-[120%] left-0 text-left border ${
              darkMode
                ? "bg-white border-neutral-300"
                : "bg-[#202020] border-neutral-500"
            } w-full z-10`}
          >
            <li
              className={`pl-2 pt-2 ${
                darkMode ? "text-neutral-400" : "text-neutral-500"
              }`}
            >
              {"Smart (more credit)"}
            </li>
            <li
              key={"Claude 3.7"}
              className={`flex flex-row rounded-xl gap-1 m-1 items-center py-2 pl-3 ${
                darkMode ? "hover:bg-neutral-200" : "hover:bg-neutral-500"
              } cursor-pointer`}
              onClick={() => handleOptionClick("Claude 3.7")}
            >
              <div className="flex w-4 h-4 rounded-full bg-orange-400 items-center justify-center">
                <SiClaude size={"12px"} color="white" />
              </div>
              {"Claude 3.7"}
              {value === "Claude 3.7" && (
                <span className="text-green-500">✔</span>
              )}
            </li>
            <li
              key={"GPT-4o"}
              className={`flex flex-row rounded-xl gap-1 m-1 items-center py-2 pl-3 ${
                darkMode ? "hover:bg-neutral-200" : "hover:bg-neutral-500"
              } cursor-pointer`}
              onClick={() => handleOptionClick("GPT-4o")}
            >
              <div className="flex w-4 h-4 rounded-full bg-neutral-600 items-center justify-center">
                <SiOpenai size={"12px"} color="white" />
              </div>
              {"GPT-4o"}
              {value === "GPT-4o" && (
                <span className="ml-4 text-green-500">✔</span>
              )}
            </li>
            <li
              className={`pl-2 ${
                darkMode ? "text-neutral-400" : "text-neutral-500"
              }`}
            >
              {"Cheap"}
            </li>
            <li
              key={"DeepSeek"}
              className={`flex flex-row rounded-xl gap-1 m-1 items-center py-2 pl-3 ${
                darkMode ? "hover:bg-neutral-200" : "hover:bg-neutral-500"
              } cursor-pointer`}
              onClick={() => handleOptionClick("DeepSeek")}
            >
              <div className="flex w-4 h-4 rounded-full bg-slate-300 items-center justify-center border border-blue-500">
                <DeepseekIcon />
              </div>
              {"DeepSeek"}
              {value === "DeepSeek" && (
                <span className="ml-1 text-green-500">✔</span>
              )}
            </li>
          </ul>
        )}
      </div>
    );
  };

  const options = [
    { value: "Claude 3.7", text: "Claude 3.7" },
    { value: "GPT-4o", text: "GPT-4o" },
    { value: "DeepSeek", text: "DeepSeek" },
  ];

  return (
    <div
      ref={chatContainerRef}
      className={`flex w-[800px] h-[600px] rounded-xl overflow-hidden ${
        darkMode
          ? "bg-[#f1f5f8] border-neutral-700"
          : "bg-[#292929] border-neutral-400"
      } 
        ${isFullscreen ? "fixed top-0 left-0 w-full h-full z-50" : ""}`}
    >
      {!isSidebarCollapsed && (
        <aside
          className={`w-[150px] animate-slideLeftAndFade ${
            darkMode
              ? "bg-white border-neutral-300"
              : "bg-[#202020] border-neutral-700"
          } p-2 border-r-[1px] flex flex-col`}
        >
          <div className={`flex items-center justify-between mb-2 h-[30px]`}>
            <button
              onClick={() => setIsSidebarCollapsed(true)}
              className={`${
                darkMode
                  ? "text-neutral-600 hover:text-neutral-800"
                  : "text-neutral-400 hover:text-neutral-200"
              } hover:bg-gray-200 p-1 rounded`}
            >
              <PanelLeftClose className="w-4 h-4" />
            </button>
            <button
              className={`${
                darkMode
                  ? "text-neutral-600 hover:text-neutral-800"
                  : "text-neutral-400 hover:text-neutral-200"
              } hover:bg-gray-200 p-1 rounded`}
              onClick={handleNewChat}
            >
              <PlusCircle className="w-4 h-4" />
            </button>
          </div>

          {/* Chat History */}
          <div className="flex-1 overflow-y-auto mt-3">
            <div
              className={`text-xs ${
                darkMode ? "text-[#9AA1A9]" : "text-neutral-400"
              } mb-2 text-left`}
            >
              Chat History
            </div>
            <div className="flex flex-col gap-1">
              {chatHistory.map((chat, idx) => (
                <button
                  key={chat.time}
                  className={`nodrag flex flex-col p-2 rounded-2xl text-left ${
                    darkMode
                      ? "bg-[#d0d1d1] hover:bg-gray-300"
                      : "bg-[#404040] hover:bg-[#4e4e4e] text-neutral-200"
                  }`}
                  onClick={() => {
                    setMessages(chat.content);
                    setSelectedModel(chat.model);
                  }}
                >
                  <p
                    className={`ml-1 text-xs ${
                      darkMode
                        ? "bg-[#d0d1d1] hover:bg-gray-200 text-neutral-800"
                        : "bg-[#404040] hover:bg-[#4e4e4e] text-neutral-200"
                    } max-w-[100px] focus-within:outline-none`}
                  >
                    {chat.title.value ? chat.title.value : chat.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`w-[40px] text-[9px] px-0.5 py-0.5 rounded ${chat.color}`}
                    >
                      {chat.model}
                    </span>
                    <p
                      className={`w-[50px] text-[7px] ml-1.5 ${
                        darkMode ? "text-[#323538]" : "text-neutral-400"
                      }`}
                    >
                      {chat.time}
                    </p>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHistoryDelClick(idx);
                      }}
                      className="hover:bg-gray-300 ml-1.5 p-1 rounded"
                    >
                      <RiDeleteBin6Line color="gray" size={11} />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </aside>
      )}
      {/* Main Chat Section */}
      <main className="flex-1 flex flex-col">
        <header
          className={`h-[44px] flex items-center justify-between p-2 px-4 ${
            darkMode ? "bg-white" : "bg-[#202020]"
          } text-white font-semibold`}
        >
          <div className="flex items-center justify-start">
            {isSidebarCollapsed && (
              <button
                onClick={() => setIsSidebarCollapsed(false)}
                className={`${
                  darkMode
                    ? "text-neutral-600 hover:text-neutral-800"
                    : "text-neutral-400 hover:text-neutral-200"
                } hover:bg-gray-200 p-1 rounded mr-2`}
              >
                <PanelLeft className="w-4 h-4" />
              </button>
            )}
            {/* AI Model Select */}
            <div
              className={`flex flex-row items-center justify-center rounded-3xl border ${
                darkMode ? "border-neutral-400" : "border-neutral-400"
              }`}
            >
              <CustomSelect
                options={options}
                value={selectedModel}
                onChange={(value) => setSelectedModel(value)}
                darkMode={darkMode}
              />
            </div>
          </div>
          <FullscreenIcon
            className={`w-6 h-6 ${
              darkMode
                ? "text-neutral-600 hover:text-neutral-950"
                : "text-neutral-400 hover:text-neutral-200"
            } hover:bg-gray-200 p-1 rounded cursor-pointer`}
            onClick={toggleFullscreen}
          />
        </header>

        {/* Chat Messages */}
        <div
          id={`${chatNodeId}chatMessages`}
          className={`flex-auto p-3 overflow-y-auto h-[450px]`}
        >
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex gap-2 mb-2 items-start ${
                msg.role === "user" ? "justify-end" : ""
              }`}
            >
              {msg.role === "bot" && (
                <div className="flex-col flex items-center justify-center text-[28px]">
                  <div
                    className={`w-[30px] h-[30px] text-base py-1 ${
                      darkMode
                        ? "bg-[#e2e6e9] border border-neutral-300"
                        : "bg-gray-400 bg-opacity-10 border-neutral-200 border-opacity-30"
                    } rounded-xl flex items-center justify-center shrink-0`}
                  >
                    🤖
                  </div>
                  <div
                    className={`w-[50px] text-[8px] ${
                      darkMode ? "text-neutral-700" : "text-neutral-400"
                    } mt-1`}
                  >
                    {msg.time}
                  </div>
                </div>
              )}
              <div id={`${chatNodeId}${idx}`}>
                <div
                  className={`nodrag hover:cursor-text hover:select-text text-left font-sans text-xs flex flex-col items-start justify-center px-2 py-1.5 rounded-2xl ${
                    msg.role === "user"
                      ? `max-w-[500px] min-w-[50px] mr-0.5 ${
                          darkMode
                            ? "bg-white border border-neutral-300 text-[#343A40] hover:bg-gray-200"
                            : "bg-green-600 bg-opacity-10 text-green-200 text-opacity-90 border-green-200 border-opacity-30 hover:bg-green-700"
                        }`
                      : `max-w-[90%] overflow-x-auto ml-0.5 ${
                          darkMode
                            ? "bg-[#e2e6e9] border border-neutral-300 text-[#343A40] hover:bg-gray-300"
                            : "bg-gray-600 bg-opacity-10 text-neutral-100 text-opacity-90 border-neutral-200 border-opacity-30 hover:bg-gray-700"
                        }`
                  } break-words whitespace-pre-wrap leading-relaxed`}
                >
                  <ReactMarkdown
                    children={msg.text}
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                  />
                </div>
              </div>

              {msg.role === "user" && (
                <div className="flex-col flex items-center justify-center text-[20px]">
                  <div
                    className={`w-[30px] h-[30px] rounded-xl ${
                      darkMode
                        ? "bg-[#F8F9FA] border border-neutral-300"
                        : "bg-green-600 bg-opacity-10 border-green-200 border-opacity-30"
                    } flex items-center justify-center shrink-0`}
                  >
                    <FaRegUser size={18} color="gray" />
                  </div>
                  <div
                    className={`w-[50px] text-[8px] ${
                      darkMode ? "text-neutral-700" : "text-neutral-400"
                    } mt-1`}
                  >
                    {msg.time}
                  </div>
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex ml-2 justify-start">
              <div className="flex-col flex items-center justify-center text-[28px]">
                <div
                  className={`w-[30px] h-[30px] text-base py-1 ${
                    darkMode
                      ? "bg-[#F8F9FA]"
                      : "bg-gray-600 bg-opacity-10 border-neutral-200 border-opacity-30"
                  } rounded-xl items-center justify-center shrink-0`}
                >
                  🤖
                </div>
              </div>
              <div className="pl-3 pb-3 pt-3">
                <BiLoader
                  size={"18"}
                  className="loading-icon"
                  color={darkMode ? "black" : "white"}
                />
              </div>
            </div>
          )}
        </div>

        {/* Message Input Bar */}
        <div
          className={`p-3 gap-1.5 ${
            darkMode ? "bg-white" : "bg-[#202020] bg-opacity-[1]"
          } flex items-center`}
        >
          <div className="relative flex-1">
            <div
              className={`flex items-center justify-stretch ${
                darkMode
                  ? "bg-[#f5f8fa] border-neutral-300 text-black"
                  : "bg-[#292929] border-[#292929] text-neutral-200"
              } focus:border-neutral-600 border-[1px] mr-10`}
            >
              <textarea
                ref={textAreaRef}
                className={`centered-placeholder nodrag w-[90%] h-[36px] min-h-[36px] max-h-[120px] ${
                  darkMode
                    ? "bg-[#f5f8fa] border-neutral-300 text-black"
                    : "bg-[#292929] border-[#292929] text-neutral-200"
                } text-sm font-sans p-1.5 pr-3 rounded-sm focus-within:outline-none resize-y`}
                placeholder={
                  isRecording
                    ? "Recording..."
                    : isConverting
                    ? "Converting..."
                    : "Message..."
                }
                value={input}
                onChange={(e) => handleInputChange(e)}
                onKeyDown={(e) => handleEnterPress(e)}
              />
              <button
                className={`nodrag absolute right-[60px] top-1/2 transform -translate-y-1/2 ${
                  darkMode
                    ? "text-[#9AA1A9] hover:text-[#343A40]"
                    : "text-neutral-400 hover:text-neutral-200"
                } hover:bg-gray-200 p-1 rounded`}
                onClick={sendAndReceiveMsg}
              >
                <Send size={18} />
              </button>
            </div>

            <button
              className={`nodrag absolute right-1.5 top-1/2 transform -translate-y-1/2 ${
                darkMode
                  ? "text-[#9AA1A9] hover:text-[#343A40]"
                  : "text-neutral-400 hover:text-neutral-200"
              } hover:bg-gray-200 p-1 rounded`}
              onClick={
                isRecording
                  ? () => {
                      stopRecording();
                    }
                  : startRecording
              }
            >
              {isRecording ? (
                <FaRegStopCircle
                  className="pumping-icon"
                  size={18}
                  color="red"
                />
              ) : (
                <FaMicrophone size={18} />
              )}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChatInterface;
